// ============================================================
// FIDDLE THEORY ENGINE
// Tuning: G3=7, D4=2, A4=9, E5=4 — identical to the mandolin, so every
// scale/arpeggio/double-stop formula ports across unchanged.
//
// What is NOT shared with the mandolin is geometry. A fiddle has no frets,
// so a note's position cannot be an integer index into an evenly-spaced grid:
// it is a real distance from the nut, and that distance compresses as you
// climb. Everything in the "Geometry" section below exists for that reason.
// ============================================================

import {
  noteIndex, usesFlats, getNoteName, MODES, ALL_KEYS,
  ARPEGGIO_DICT, spellChordTone,
} from './theory';
import { getGuitarTitle, getNotesInKey, getNotesInChord, notesHeading } from './notes-row';

// ── Tuning & layout constants ────────────────────────────────────────────────

// String indices: 0 = G (lowest), 1 = D, 2 = A, 3 = E (highest).
// Rendered left → right in that order, matching the violin fingering-chart
// convention (and the way a player looks down their own neck).
export const FIDDLE_TUNING       = [7, 2, 9, 4];
export const FIDDLE_STRING_NAMES = ['G', 'D', 'A', 'E'];

// Charted range: nut (0) to the octave (12). Semitone 12 is the neck joint —
// on a fretless board it is the one landmark you can find by feel, which is
// why it makes a better stopping point than an arbitrary "20th fret".
export const TOTAL_SEMITONES_FIDDLE = 12;

// Vibrating string length of a full-size (4/4) violin, in millimetres.
// Only used for distances in millimetres (semitoneMm); the note geometry is scale-invariant.
export const FIDDLE_SCALE_LENGTH_MM = 328;

// Where a beginner tapes the fingerboard: a whole step, major 3rd, perfect 4th
// and perfect 5th above each open string. These are FIXED TO THE INSTRUMENT —
// they do not move when the key changes, and seeing that is the point.
export const FIDDLE_TAPE_SEMITONES = [2, 4, 5, 7];

// ── Degree & formula tables ──────────────────────────────────────────────────

export const FIDDLE_DEGREE_LABELS = {
  0: '1', 1: 'b2', 2: '2', 3: 'b3', 4: '3',  5: '4',
  6: 'b5', 7: '5', 8: 'b6', 9: '6', 10: 'b7', 11: '7',
};

// Tonality is applied via resolveFiddleFormula() — per the tonality convention,
// Major/Minor never appear in a structure option list.
export const FIDDLE_FORMULAS = {
  'Major':          [0, 2, 4, 5, 7, 9, 11],
  'Minor':          [0, 2, 3, 5, 7, 8, 10],   // natural minor
  'Maj Pentatonic': [0, 2, 4, 7, 9],
  'Min Pentatonic': [0, 3, 5, 7, 10],
};

export const FIDDLE_STRUCTURE_OPTIONS = ['Full Scale', 'Pentatonic', 'Double Stops', 'Modes', 'Arpeggios'];

// Semitone delta between the two strings of a double stop. Adjacent strings are
// a perfect 5th (7 semitones) apart, so the same stopped distance on both
// strings sounds a 5th; everything else is measured from there.
export const FIDDLE_DOUBLE_STOP_DELTAS = {
  '3rds': [-4, -3],   // major 3rd / minor 3rd
  '4ths': [-2],
  '5ths': [0],
  '6ths': [2],
};

export const FIDDLE_DOUBLE_STOP_OPTIONS = ['3rds', '4ths', '5ths', '6ths'];

// ── Positions ────────────────────────────────────────────────────────────────
//
// A violin position is named for the scale degree the FIRST FINGER takes above
// the open string: 1st position → the 2nd degree, 2nd position → the 3rd,
// 3rd position → the 4th. `low`/`high` bound what the hand can reach without
// shifting, including the low-1st-finger and high-4th-finger extensions.
//
// `null` (Full Neck) means no windowing at all.
export const FIDDLE_POSITIONS = {
  '1st':       { base: 2, low: 1, high: 8  },
  '2nd':       { base: 4, low: 3, high: 10 },
  '3rd':       { base: 5, low: 4, high: 12 },
  'Full Neck': null,
};

export const FIDDLE_POSITION_OPTIONS = ['1st', '2nd', '3rd', 'Full Neck'];

/**
 * Which finger stops a given semitone in a given position.
 *
 * Fingers are laid out in pairs from the position's base note — 1st finger
 * covers base-1 and base, 2nd covers base+1 and base+2, and so on — which is
 * how a hand frame actually works: each finger has a low and a normal placing.
 *
 * @param {number} semitone   - semitones above the open string (0 = open)
 * @param {string} positionName - key of FIDDLE_POSITIONS
 * @returns {number|null} 0-4, or null when the note is outside the hand frame
 */
export function fingerFor(semitone, positionName) {
  if (semitone === 0) return 0;                 // open strings are always available
  const pos = FIDDLE_POSITIONS[positionName];
  if (!pos) return null;                        // Full Neck — no fingering to show
  if (semitone < pos.low || semitone > pos.high) return null;
  return Math.min(4, 1 + Math.floor((semitone - pos.base + 1) / 2));
}

/** True when a semitone falls inside the position's hand frame (or is open). */
export function isInPosition(semitone, positionName) {
  if (semitone === 0) return true;
  const pos = FIDDLE_POSITIONS[positionName];
  if (!pos) return true;
  return semitone >= pos.low && semitone <= pos.high;
}

// ── Geometry ─────────────────────────────────────────────────────────────────
//
// THE important part of this file. On a fretted instrument a note's position is
// an integer and the renderer can space frets however it likes. On a fiddle the
// distance from the nut to the note n semitones up is fixed by physics:
//
//     d(n) = L × (1 − 2^(−n/12))
//
// so the half step at the nut is nearly twice the width of the half step an
// octave up. Rendering these evenly does not simplify the instrument, it
// misrepresents it — a player practising against an evenly-spaced chart is
// learning the wrong hand shape.

/** Distance from the nut to semitone n, as a fraction of the string length. */
export function semitoneRatio(n) {
  return 1 - Math.pow(2, -n / 12);
}

/**
 * Distance from the nut to semitone n, normalised across the charted range
 * (0 at the nut, 1 at the octave). This is what the renderer multiplies by the
 * board height. d(12) is exactly L/2, hence the doubling.
 */
export function boardFraction(n) {
  return semitoneRatio(n) * 2;
}

/** Distance from the nut to semitone n in millimetres. */
export function semitoneMm(n, scaleLength = FIDDLE_SCALE_LENGTH_MM) {
  return scaleLength * semitoneRatio(n);
}

// ── Formula resolution ───────────────────────────────────────────────────────

/**
 * Resolve structure + mode + tonality into a semitone-interval array.
 * Tonality is never part of the structure list — it is always a separate toggle.
 */
export function resolveFiddleFormula(structure, mode, tonality) {
  const isMajor = tonality !== 'Minor';
  switch (structure) {
    case 'Pentatonic':
      return FIDDLE_FORMULAS[isMajor ? 'Maj Pentatonic' : 'Min Pentatonic'];
    case 'Modes':
      return MODES[mode] ?? MODES['Ionian'];
    case 'Double Stops':
    case 'Full Scale':
    default:
      return FIDDLE_FORMULAS[isMajor ? 'Major' : 'Minor'];
  }
}

// ── Builder: scale tones ─────────────────────────────────────────────────────

/**
 * Every scale tone on the charted board.
 *
 * Notes outside the active position are returned with `inPosition: false`
 * rather than dropped, so the renderer can dim them — on a fretless board it
 * matters that you can see where the next shift lands.
 *
 * @returns {Array<{string, semitone, isRoot, interval, noteName, finger, inPosition}>}
 */
export function buildFiddleNodes(rootName, formula, tonality, position, stringFocus) {
  const root = noteIndex(rootName);
  if (root === -1) return [];

  const flats      = usesFlats(rootName, formula, (tonality || 'Major').toLowerCase());
  const formulaSet = new Set(formula);
  const nodes      = [];

  for (let s = 0; s < FIDDLE_TUNING.length; s++) {
    if (!stringFocus[s]) continue;
    for (let n = 0; n <= TOTAL_SEMITONES_FIDDLE; n++) {
      const pitch    = (FIDDLE_TUNING[s] + n) % 12;
      const interval = (pitch - root + 12) % 12;
      if (!formulaSet.has(interval)) continue;
      nodes.push({
        string:     s,
        semitone:   n,
        isRoot:     interval === 0,
        interval,
        noteName:   getNoteName(pitch, flats),
        finger:     fingerFor(n, position),
        inPosition: isInPosition(n, position),
      });
    }
  }
  return nodes;
}

// ── Builder: arpeggios ───────────────────────────────────────────────────────

/**
 * Every chord tone of an arpeggio on the charted board.
 *
 * Shares ARPEGGIO_DICT and spellChordTone() with the guitar, so a type means
 * exactly the same thing on both instruments and the enharmonic spelling agrees
 * (Eb dim7 gives Dbb here just as it does there).
 *
 * Returns the SAME node shape as buildFiddleNodes() so the renderer needs no
 * special case, with one addition: `degree` carries the chord-tone label from
 * the dictionary ('b3', 'bb7'). `interval` stays numeric because the position
 * logic and the Triads Only filter both index on it — the two fields describe
 * the same tone, one for arithmetic and one for display.
 *
 * @returns {Array<{string, semitone, isRoot, interval, degree, noteName, finger, inPosition}>}
 */
export function buildFiddleArpeggioNodes(rootName, type, tonality, position, stringFocus) {
  const entry = ARPEGGIO_DICT[`${type}:${tonality === 'Minor' ? 'Minor' : 'Major'}`];
  if (!entry) return [];
  const root = noteIndex(rootName);
  if (root === -1) return [];

  const { intervals, labels } = entry;

  // Precompute per pitch class so the board scan below is a lookup, not a search.
  const byPitch = {};
  intervals.forEach(off => {
    byPitch[(root + off) % 12] = {
      interval: off,
      degree:   labels[off],
      noteName: spellChordTone(rootName, off, labels[off]),
    };
  });

  const nodes = [];
  for (let s = 0; s < FIDDLE_TUNING.length; s++) {
    if (!stringFocus[s]) continue;
    for (let n = 0; n <= TOTAL_SEMITONES_FIDDLE; n++) {
      const tone = byPitch[(FIDDLE_TUNING[s] + n) % 12];
      if (!tone) continue;
      nodes.push({
        string:     s,
        semitone:   n,
        isRoot:     tone.interval === 0,
        interval:   tone.interval,
        degree:     tone.degree,
        noteName:   tone.noteName,
        finger:     fingerFor(n, position),
        inPosition: isInPosition(n, position),
      });
    }
  }
  return nodes;
}

// ── Builder: double stops ────────────────────────────────────────────────────

/**
 * Scale-tone pairs on adjacent strings that form the requested interval.
 * Returns the same node shape as buildFiddleNodes() so the renderer can treat
 * them identically, plus the pairs themselves for drawing the connecting bar.
 *
 * @returns {{nodes: Array, pairs: Array<{sLow, nLow, sHigh, nHigh}>}}
 */
export function buildFiddleDoubleStopNodes(rootName, formula, tonality, intervalName, position, stringFocus) {
  const root = noteIndex(rootName);
  if (root === -1) return { nodes: [], pairs: [] };

  const flats      = usesFlats(rootName, formula, (tonality || 'Major').toLowerCase());
  const formulaSet = new Set(formula);
  const deltas     = FIDDLE_DOUBLE_STOP_DELTAS[intervalName] ?? [0];

  // In-scale semitones per string (ignoring string focus for now — a pair needs
  // both of its strings, and that is checked when the pairs are assembled).
  const inScale = FIDDLE_TUNING.map(open => {
    const set = new Set();
    for (let n = 0; n <= TOTAL_SEMITONES_FIDDLE; n++) {
      if (formulaSet.has(((open + n) - root + 144) % 12)) set.add(n);
    }
    return set;
  });

  const pairs = [];
  for (let s = 0; s < FIDDLE_TUNING.length - 1; s++) {
    if (!stringFocus[s] || !stringFocus[s + 1]) continue;
    inScale[s].forEach(nLow => {
      deltas.forEach(delta => {
        const nHigh = nLow + delta;
        if (nHigh < 0 || nHigh > TOTAL_SEMITONES_FIDDLE) return;
        if (!inScale[s + 1].has(nHigh)) return;
        // Both notes must be reachable without shifting, or it is not a double stop.
        if (!isInPosition(nLow, position) || !isInPosition(nHigh, position)) return;
        pairs.push({ sLow: s, nLow, sHigh: s + 1, nHigh });
      });
    });
  }

  // Deduplicate the note positions the pairs touch.
  const seen  = new Set();
  const nodes = [];
  pairs.forEach(({ sLow, nLow, sHigh, nHigh }) => {
    [[sLow, nLow], [sHigh, nHigh]].forEach(([s, n]) => {
      const key = `${s}-${n}`;
      if (seen.has(key)) return;
      seen.add(key);
      const pitch    = (FIDDLE_TUNING[s] + n) % 12;
      const interval = (pitch - root + 12) % 12;
      nodes.push({
        string:     s,
        semitone:   n,
        isRoot:     interval === 0,
        interval,
        noteName:   getNoteName(pitch, flats),
        finger:     fingerFor(n, position),
        inPosition: true,
      });
    });
  });

  return { nodes, pairs };
}

// ── Half-step detection ──────────────────────────────────────────────────────

/**
 * Consecutive scale tones on the same string that are one semitone apart — the
 * places where two fingers actually touch.
 *
 * This is the one thing a fretted diagram structurally cannot show: there,
 * every gap between adjacent dots looks the same, so a half step and a whole
 * step are indistinguishable without counting frets.
 *
 * @returns {Array<{string, low, high}>}
 */
export function findHalfStepPairs(nodes) {
  const byString = new Map();
  nodes.forEach(n => {
    if (!byString.has(n.string)) byString.set(n.string, []);
    byString.get(n.string).push(n.semitone);
  });

  const pairs = [];
  byString.forEach((semitones, string) => {
    const sorted = [...semitones].sort((a, b) => a - b);
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i + 1] - sorted[i] === 1) {
        pairs.push({ string, low: sorted[i], high: sorted[i + 1] });
      }
    }
  });
  return pairs;
}

// ── Page title and notes row ─────────────────────────────────────────────────

/**
 * Page title for the fiddle: the guitar titles ("G Major Scale", "G Minor Pentatonic",
 * "G Dorian", "G Maj7 Arpeggio") plus "G Major Double Stops".
 *
 * @param {object} p
 * @param {string} p.key
 * @param {string} p.tonality 'Major' | 'Minor'
 * @param {string} p.structure one of FIDDLE_STRUCTURE_OPTIONS
 * @param {string} [p.mode]
 * @param {string} [p.arpeggioType]
 * @returns {string}
 */
export function getFiddleTitle({ key, tonality, structure, mode, arpeggioType }) {
  if (structure === 'Double Stops') return `${key} ${tonality === 'Minor' ? 'Minor' : 'Major'} Double Stops`;
  return getGuitarTitle({ key, tonality, structure, mode, arpeggioType });
}

/**
 * The notes row for the fiddle (heading from notesHeading). Double Stops shows the scale
 * the pairs are drawn from.
 *
 * @returns {{heading: string, notes: Array<{name: string, degree: string, isRoot: boolean}>}}
 */
export function getFiddleNotesRow({ key, tonality, structure, mode, arpeggioType }) {
  if (structure === 'Arpeggios') {
    return { heading: notesHeading(structure), notes: getNotesInChord({ key, tonality, arpeggioType }) };
  }
  const rowStructure = structure === 'Double Stops' ? 'Full Scale' : structure;
  return { heading: notesHeading(structure), notes: getNotesInKey({ key, tonality, structure: rowStructure, mode }) };
}

export { ALL_KEYS };
